// 보완요청 메일 초안 — 순수 로직(테스트 대상). SupplementReview.vue 에서 사용한다.
// node:test 로 단위 테스트하기 위해 CommonJS(require 가능)로 작성한다.
/* global globalThis */  // ES2020 표준 전역(Node·브라우저 공통) — env 미선언이라 명시

// 백엔드 _assign_group_keys 와 동일 규칙으로 feedback_group_key 를 재계산한다.
// 같은 문항 그룹(question_group_key) 안에서 피드백 문구가 완전히 동일할 때만 같은
// 키를 부여해 병합하고, 문구가 달라지면(=편집) 자동으로 병합이 풀린다. 빈 문구는
// 기본값으로 채워 빈 셀/스키마 minLength 오류를 방지한다.
// (백엔드가 PATCH 에서 최종 authoritative 하게 재계산하지만, 배포 순서와 무관하게
//  프론트 단독으로도 병합 편집 소실을 막기 위한 방어선이다.)
function normalizeRowKeys(rows) {
    const keyMap = {};
    let seq = 0;
    return (rows || []).map((r) => {
        const feedback = (r.feedback_text || '').trim() || '보완이 필요합니다.';
        const groupScope = r.question_group_key || `q-${r.source_sub_code}`;
        const mapKey = JSON.stringify([groupScope, feedback]);
        if (keyMap[mapKey] === undefined) {
            seq += 1;
            keyMap[mapKey] = `fb-${seq}`;
        }
        return {...r, feedback_text: feedback, feedback_group_key: keyMap[mapKey]};
    });
}

// dirty 판정용 — 편집 가능한 필드(제목/수신자목록/기한/URL/HTML/행 피드백)만 직렬화한다.
// 행 재정렬/제외/피드백 수정, 수신자 추가/제거가 모두 반영된다.
// recipient_name 은 본문에서 제거돼 더 이상 편집 대상이 아니므로 제외한다.
function serializeDraftFields(draft) {
    if (!draft) return '';
    return JSON.stringify({
        subject: draft.subject || '',
        subject_en: draft.subject_en || '',
        supplement_deadline: draft.supplement_deadline || '',
        system_url: draft.system_url || '',
        body_html: draft.body_html || '',
        body_html_en: draft.body_html_en || '',
        recipients: (draft.recipients || []).map((r) => ({
            e: (r.email || '').trim(), r: r.role == null ? '' : r.role, s: r.source || '',
        })),
        rows: (draft.rows || []).map((r) => ({s: r.source_sub_code, f: r.feedback_text || ''})),
    });
}

// Vue 2 가 본문/영문 필드를 최초 렌더부터 반응형으로 관찰하도록 응답 형태를 정규화한다.
// 영문(subject_en/body_html_en)은 서버가 NULL 로 내려도 '' 로 둬 v-model 이 바로 붙는다.
function normalizeDraft(draft) {
    const d = draft || {};
    return {
        ...d,
        body_html: d.body_html || '',
        subject_en: d.subject_en || '',
        body_html_en: d.body_html_en || '',
    };
}

// 영문은 '' 로 보내면 백엔드가 NULL 로 정규화한다(영문 없음). 국문/영문을 PATCH 1회로 저장.
function buildDraftPayload(draft) {
    return {
        subject: draft.subject || '',
        subject_en: draft.subject_en || '',
        recipient_name: draft.recipient_name || null,
        supplement_deadline: draft.supplement_deadline || null,
        system_url: draft.system_url || null,
        body_html: draft.body_html || '',
        body_html_en: draft.body_html_en || '',
        recipients: draft.recipients || [],
        rows: draft.rows || [],
    };
}

// 임의 수신자 입력 검사 → null(추가 가능) | 'empty' | 'duplicate'(대소문자 무시) | 'invalid'(형식).
// 형식은 오타를 거르는 정도만 본다 — 도달 여부는 검증하지 않는다(요구사항).
function validateManualRecipient(recipients, email) {
    const e = (email || '').trim();
    if (!e) return 'empty';
    if ((recipients || []).some((r) => (r.email || '').trim().toLowerCase() === e.toLowerCase())) {
        return 'duplicate';
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? null : 'invalid';
}

// 임의 수신자 추가 — 이메일 trim, 검사(validateManualRecipient)에 걸리면 원래 배열을 그대로,
// 통과하면 새 배열을 반환한다. source='manual', role=null(수동 추가).
function addManualRecipient(recipients, email) {
    const list = recipients || [];
    if (validateManualRecipient(list, email)) return list;
    return [...list, {email: email.trim(), name: null, role: null, source: 'manual'}];
}

// 문항이 메일 표에 들어가는지 — 명시 확정(include_in_email)이 파생값보다 우선(BE _included 와 동일).
function isIncludedInEmail(item) {
    if (item.include_in_email !== null && item.include_in_email !== undefined) {
        return !!item.include_in_email;
    }
    return item.current_status === 'SUPPLEMENT_REQUIRED';
}

// 초안 표(rows)가 현재 문항 판정과 어긋났는지 — 메일 포함 문항 집합이나 피드백 문구가 다르면 true.
// 표는 초안 생성 때 본문에 굳어(BE build_email_rows) 문항을 고쳐도 메일 표는 그대로라 알려야 한다.
// 피드백 비교는 BE _assign_group_keys 와 같은 정규화(trim, 빈 값은 기본 문구)로 한다.
function isDraftRowsOutdated(rows, items) {
    const norm = (t) => (t || '').trim() || '보완이 필요합니다.';
    const want = new Map((items || []).filter(isIncludedInEmail)
        .map((it) => [it.sub_code, norm(it.feedback_text)]));
    const have = rows || [];
    if (have.length !== want.size) return true;
    return have.some((r) => want.get(r.source_sub_code) !== norm(r.feedback_text));
}

// 잡 상태·처리 코드(BE SupplementReviewJob.STATUS_*/ERR_*) → 화면 문구. 문항 error_code 도 같은 코드다.
// JOB_STATUS_LABEL 은 목록(SupplementReviewList jobStatusLabel)과 같은 값.
const JOB_STATUS_LABEL = {
    PENDING: '진행 중', RUNNING: '진행 중', FINALIZING: '진행 중',
    DONE: '완료', PARTIAL: '부분 완료', FAILED: '실패',
};
const JOB_ERROR_LABEL = {
    PACKET_SCOPE_NOT_FOUND: '이 업체의 판정 대상 문항을 찾지 못했습니다.',
    NO_ITEMS: '판정 대상 문항이 없습니다.',
    RATE_LIMITED: 'AI 요청 한도 초과 — 잠시 후 다시 요청하세요.',
    QUOTA_EXHAUSTED: 'AI 사용 한도 소진 — 개발팀에 문의하세요.',
    ALL_PROVIDERS_FAILED: 'AI 응답 오류 — 다시 요청하세요.',
    SCHEMA_INVALID: 'AI 응답 오류 — 다시 요청하세요.',
    ATTACHMENT_OVERLOAD: '첨부·답변이 판정 상한을 넘었습니다.',
    ANSWER_OVERLOAD: '첨부·답변이 판정 상한을 넘었습니다.',
    CROSS_CHECK_FAILED: '문항 간 정합성 검토만 실패했습니다(판정은 완료).',
    INTERNAL_ERROR: '내부 오류 — 개발팀에 문의하세요.',
};

function jobStatusLabel(status) {
    return JOB_STATUS_LABEL[status] || status || '-';
}

// 모르는 코드는 숨기지 않고 코드 그대로 괄호로 보인다(개발팀 문의 시 단서).
function jobErrorLabel(code) {
    if (!code) return '';
    return JOB_ERROR_LABEL[code] || `알 수 없는 처리 결과 (${code})`;
}

const CONFIDENCE_LABEL = {high: '확신도 높음', medium: '확신도 보통', low: '확신도 낮음'};

// 발송 후 평가상태 전환을 건너뛴 사유(BE reopen.skipped_reason) → 문구.
const REOPEN_SKIP_LABEL = {
    status_not_reopenable: '현재 평가 상태에서는 답변 가능으로 바꿀 수 없어 전환을 건너뛰었습니다.',
};

// display_order 오름차순 in-place 정렬 — 제외한 행 복원 시 원래 순서를 유지한다.
function sortRowsByDisplayOrder(rows) {
    rows.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return rows;
}

// 날짜/시간 피커 값 → API가 기대하는 로컬 일시 문자열.
// 날짜 없음 → '' (자동 기한), 날짜만 → 'YYYY-MM-DD', 둘 다 → 'YYYY-MM-DD HH:mm'.
function combineDeadline(date, time) {
    if (!date) return '';
    return time ? `${date} ${time.slice(0, 5)}` : date;
}

// textarea 선택 범위를 변수로 치환하고 삽입 직후 커서 위치를 계산한다.
function insertTextAtSelection(text, start, end, insertion) {
    const source = text || '';
    return {
        text: source.slice(0, start) + insertion + source.slice(end),
        caret: start + insertion.length,
    };
}

// 저장된 명시 기한만 복원, 없으면 비움(자동 기한은 서버가 계산·표시).
// 날짜만 저장된 값('YYYY-MM-DD')은 time:'' 로 복원한다. 두 번째 인자는 하위호환용으로 무시.
function resolveDeadlineParts(deadline) {
    const match = String(deadline || '').trim()
        .match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?/);
    return match ? {date: match[1], time: match[2] || ''} : {date: '', time: ''};
}

// 백엔드 resolve_default_system_url 의 최종 폴백과 동일. 레거시 null 초안 방어용.
const DEFAULT_SYSTEM_URL = 'https://qesg.tierzero.kr';

// 본문 토큰 4종의 실제 값 맵(알약 표시용, escape 안 함). 백엔드 _render_email_draft_body 와
// 같은 우선순위 — 기한은 명시값 없으면 서버 계산값, URL 은 없으면 기본 URL.
// locale='en' 이면 캠페인/업체명은 draft 의 *_en **만** 쓴다 — 서버(_draft_to_dict)가 국문
// 폴백을 이미 적용해 내려주므로 클라이언트 폴백은 없고, meta(로스터, 국문뿐)도 보지 않는다.
// 빈 문자열이면 그대로 빈 문자열(알약은 라벨로 폴백).
function buildVariableValues(draft, meta, locale = 'ko') {
    const d = draft || {};
    const m = meta || {};
    const en = locale === 'en';
    return {
        campaign_name: en ? (d.campaign_display_name_en || '')
            : (m.campaignName || d.campaign_display_name || ''),
        company_name: en ? (d.company_name_en || '')
            : (m.companyName || d.company_name || ''),
        supplement_deadline: d.supplement_deadline || d.deadline_computed || '',
        system_url: d.system_url || DEFAULT_SYSTEM_URL,
    };
}

const VARIABLE_TOKEN_RE = /\{\{\s*(campaign_name|company_name|supplement_deadline|system_url)\s*\}\}/g;

// 텍스트·큰따옴표·작은따옴표 속성 어디에 토큰이 있어도 값이 속성/태그를 탈출하지 못하게 5종 escape.
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// 미리보기용 치환 — 토큰을 HTML-escape 한 값으로 바꾼다. 값이 비면 토큰 원문을 유지한다.
// (소스 textarea/저장본은 토큰 그대로, 미리보기만 이 결과를 쓴다.)
// values 는 buildVariableValues 결과(저장 시점 스냅샷 등). null 이면 치환하지 않는다.
function substituteWithValues(bodyHtml, values) {
    const v = values || {};
    return String(bodyHtml || '').replace(VARIABLE_TOKEN_RE, (token, key) => (
        v[key] ? escapeHtml(v[key]) : token
    ));
}

function substituteVariables(bodyHtml, draft, meta) {
    return substituteWithValues(bodyHtml, buildVariableValues(draft, meta));
}

// 영문 번역 없음 목록 라벨 — translate 응답 en_placeholders / draft 응답 en_missing 공용.
// {major_category, question_name, question_text} 각각 sub_code 배열.
const EN_MISSING_LABELS = [
    ['major_category', '대분류'],
    ['question_name', '문항명'],
    ['question_text', '질문 본문'],
];

// 대분류 sub_code 배열 → 대분류 이름 배열(순서 유지 dedup). rows 로 sub_code→이름 매핑.
// FE 정규화에 따라 행 키가 source_sub_code 또는 sub_code 라서 둘 다 허용한다.
// 이름이 없으면(매칭 행 없음/이름 빈값) sub_code 로 fallback 한다.
function majorCategoryNames(codes, rows) {
    const byCode = {};
    for (const r of rows || []) {
        const code = r.source_sub_code != null ? r.source_sub_code : r.sub_code;
        if (code != null && byCode[code] === undefined) byCode[code] = r.major_category || '';
    }
    const seen = new Set();
    const names = [];
    for (const code of codes) {
        const name = byCode[code] || code;  // 이름 없으면 sub_code 로 fallback
        if (!seen.has(name)) { seen.add(name); names.push(name); }
    }
    return names;
}

// 영문 번역 없음 요약 → {total, lines[]}. 안내 모달/발송 경고/언어 행 배지가 공유.
// 대분류는 sub_code 를 대분류 이름으로 매핑 후 순서 유지 dedup 한다 — 메일 표에서 같은
// 대분류 행이 rowspan 으로 한 칸이라, 배지/경고/모달 개수도 dedup 기준이어야 표의
// '번역 없음' 칸 수와 맞는다. 문항명/질문 본문은 기존대로 sub_code 를 나열한다.
// total 은 (dedup 대분류 수 + 문항명 수 + 질문 본문 수) — 서버 en_placeholders.total 은
// "모달을 띄울지" 판단에만 쓰고, 표시 개수는 이 dedup total 을 써서 표 칸 수와 일치시킨다.
// 0개 목록은 줄에서 생략하고, 항목은 최대 8개만 나열한 뒤 나머지는 '외 M개' 로 접는다.
function summarizeEnMissing(missing, rows) {
    const m = missing || {};
    const lines = [];
    let total = 0;
    for (const [key, label] of EN_MISSING_LABELS) {
        const codes = Array.isArray(m[key]) ? m[key] : [];
        const items = key === 'major_category' ? majorCategoryNames(codes, rows) : codes;
        total += items.length;
        if (!items.length) continue;
        const shown = items.slice(0, 8).join(', ');
        const extra = items.length > 8 ? ` 외 ${items.length - 8}개` : '';
        lines.push(`${label} ${items.length}개: ${shown}${extra}`);
    }
    return {total, lines};
}

// ── 영문 발송 게이트 (§2~§5) ─────────────────────────────────────────
// 영문 표의 '번역 없음' 마커(§2)가 손대지 않은 채 몇 개 남았는지 센다(§3).
// unresolved = class 토큰에 qesg-en-missing 이 있고 && 그 요소 텍스트가(trim·NBSP→space·
// 엔티티 디코드 후) 정확히 '번역 없음' 인 요소 수. 정규식이 아니라 HTML 파서로 센다.
// 클래스는 대소문자 구분(정확 일치), 텍스트는 정확 일치. parser 는 브라우저 window.DOMParser
// (globalThis), Node 테스트는 jsdom DOMParser 를 주입한다. parser 가 없으면(순수 Node)
// 0 으로 graceful — 검사는 브라우저 전용 경로다.
const EN_MISSING_CLASS = 'qesg-en-missing';
const EN_MISSING_PLACEHOLDER_TEXT = '번역 없음';

function countUnresolvedEnPlaceholders(html, parser = globalThis.DOMParser) {
    const source = String(html || '');
    if (!source || typeof parser !== 'function') return 0;
    const doc = new parser().parseFromString(source, 'text/html');
    if (!doc) return 0;
    const nodes = doc.querySelectorAll('*');
    let count = 0;
    for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        // classList.contains 는 항상 대소문자 구분 토큰 매칭 — quirks 모드 selector 오차를 피한다.
        if (!el.classList || !el.classList.contains(EN_MISSING_CLASS)) continue;
        const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
        if (text === EN_MISSING_PLACEHOLDER_TEXT) count += 1;
    }
    return count;
}

// 실발송 payload(§4-3) — confirm 의 재오픈/override 체크와 초안 updated_at(에코, stale 검사용)을
// 담는다. draft_updated_at 은 컴포넌트가 빠뜨리지 않도록 draft 에서 직접 읽는다.
// allow_placeholders/reopen_answers 는 strict bool(BE 가 non-bool 이면 400).
function buildSendPayload(draft, {locale, reopenAnswers, allowPlaceholders} = {}) {
    return {
        locale,
        reopen_answers: !!reopenAnswers,
        allow_placeholders: !!allowPlaceholders,
        draft_updated_at: draft && draft.updated_at,
    };
}

// 발송 테스트 payload — 게이트 없음(§4-4). allow_placeholders 를 싣지 않는다.
// draft_updated_at 은 BE 가 무시하지만 계약상 동봉(§5).
function buildTestSendPayload(draft, {locale} = {}) {
    return {locale, draft_updated_at: draft && draft.updated_at};
}

// 발송 confirm 을 열 때의 초기 상태(§5) — 재오픈=on, override=off(이전 발송의 체크가 새지 않게).
function openSendConfirmState() {
    return {reopenAnswers: true, allowPlaceholders: false};
}

// '지금 발송' 비활성 여부(§5) — override 가 필요한데(영문 + 마커 잔존) 아직 체크 안 했으면 막는다.
function sendOkDisabled(needsOverride, allowPlaceholders) {
    return !!needsOverride && !allowPlaceholders;
}

// send 409 응답의 machine code(§4-3) → FE 분기 키. 그 외/비-409 는 'generic'(기존 처리).
function classifySendError(status, errorCode) {
    if (status === 409
        && (errorCode === 'en_placeholders_present' || errorCode === 'draft_stale')) {
        return errorCode;
    }
    return 'generic';
}

// send 실패 응답을 분기별로 처리한다(§5). 부작용(모달·초안 재조회·confirm 닫기)을 hooks 로 주입해
// 순수하게 테스트한다. errorInfo: {status, error, message}. hooks: {showModal, closeConfirm, reloadDraft}.
// 반환: 처리한 분기 키.
function handleSendError(errorInfo, hooks) {
    const info = errorInfo || {};
    const h = hooks || {};
    const msg = info.message || '메일 발송에 실패했습니다.';
    const branch = classifySendError(info.status, info.error);
    if (branch === 'en_placeholders_present') {
        // 저장본 기준 마커가 남음 — 저장 후 재시도 안내(재조회/닫기 없음)
        h.showModal({type: 'alert', state: 'warning', title: '번역 없음 항목 남음',
            message: `${msg} 저장 후 다시 시도해주세요.`});
    } else if (branch === 'draft_stale') {
        // 다른 곳에서 초안이 바뀜 — 재조회 후 confirm 닫기
        h.showModal({type: 'alert', state: 'warning', title: '초안 변경됨', message: msg});
        h.closeConfirm();
        h.reloadDraft();
    } else {
        h.showModal({type: 'alert', state: 'error', title: '발송 실패', message: msg});
    }
    return branch;
}

// 발송 confirm 요약 — 수신자 `이름 <email>`(앞 max명 + 나머지 수) / 실제 나갈 제목 / 기한.
// locale 은 실제 발송 언어(effectiveLocale). 영문 제목이 비면 국문 제목으로 나간다.
function buildSendSummary(draft, locale, max = 10) {
    const d = draft || {};
    const all = (d.recipients || []).map((r) => (r.name ? `${r.name} <${r.email}>` : r.email));
    const subject = (locale === 'en' && d.subject_en) ? d.subject_en : (d.subject || '');
    const deadline = d.supplement_deadline
        ? d.supplement_deadline
        : `자동: 발송일 + 2영업일${d.deadline_computed ? ` (오늘 기준 ${d.deadline_computed})` : ''}`;
    return {recipients: all.slice(0, max), moreCount: Math.max(0, all.length - max), total: all.length, subject, deadline};
}

module.exports = {
    buildSendSummary,
    normalizeRowKeys, serializeDraftFields, normalizeDraft, buildDraftPayload,
    insertTextAtSelection, sortRowsByDisplayOrder, addManualRecipient, validateManualRecipient,
    isIncludedInEmail, isDraftRowsOutdated,
    JOB_STATUS_LABEL, jobStatusLabel, jobErrorLabel, CONFIDENCE_LABEL, REOPEN_SKIP_LABEL,
    combineDeadline, resolveDeadlineParts,
    DEFAULT_SYSTEM_URL, buildVariableValues, substituteVariables, substituteWithValues,
    summarizeEnMissing,
    countUnresolvedEnPlaceholders, buildSendPayload, buildTestSendPayload,
    openSendConfirmState, sendOkDisabled, classifySendError, handleSendError,
};
